// @flow
import reportError from '@rover/utilities/reportError';
import { isAndroid, isiOS, isMobileEmbedded } from '@rover/utilities/userAgent';

type EventNames = {
  facebook_event: string,
  kochava_event: string,
  firebase_event?: string,
  branch_event?: string,
};

type EventData = {
  [string]: number | boolean | string,
};

type Event = {
  eventData: EventData,
  eventNames: EventNames,
};

type Message = { action: 'event' } & EventData;

type PostMessagePayload = Message | string;

type PostMessage = (PostMessagePayload) => void;

type RoverMessageHandlerContext = {
  postMessage: PostMessage,
};

// Use this to give errors from this function a uniform format and something we can easily search for
const reportEmbeddedError = (message: string): false =>
  reportError(`FireEmbeddedWebEventError: ${message}`) || false;

const getAndroidRover = (): RoverMessageHandlerContext =>
  // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
  window.ReactNativeWebView ? window.ReactNativeWebView : window.rover;
const getiOSRover = (): RoverMessageHandlerContext =>
  // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
  window.ReactNativeWebView
    ? window.ReactNativeWebView
    : window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.rover;

const Platforms = Object.freeze({
  android: 'android',
  ios: 'ios',
});

type Platform = $Keys<typeof Platforms>;

const getPlatform = (): ?Platform => {
  if (isAndroid()) {
    return Platforms.android;
  }
  if (isiOS()) {
    return Platforms.ios;
  }

  return null;
};

const getRover = (platform: Platform): ?RoverMessageHandlerContext => {
  switch (platform) {
    case Platforms.android:
      return getAndroidRover();
    case Platforms.ios:
      return getiOSRover();
    default:
      return null;
  }
};

const prepareEventData = (platform: Platform, message: Message): PostMessagePayload => {
  switch (platform) {
    case Platforms.android:
      return JSON.stringify(message);
    case Platforms.ios:
    default:
      return message;
  }
};

const fireEmbeddedWebEvent = ({ eventNames, eventData }: Event): boolean => {
  if (!isMobileEmbedded()) {
    return reportEmbeddedError(`"${fireEmbeddedWebEvent.name}" called outside embedded context`);
  }

  const platform = getPlatform();

  if (!platform) {
    return reportEmbeddedError('Unable to determine platform');
  }

  const rover = getRover(platform);

  if (!rover) {
    return reportEmbeddedError(
      `Unable to find embedded context object "rover" for platform: ${platform}`
    );
  }

  if (!rover.postMessage) {
    return reportEmbeddedError(
      `Embedded context did not have "postMessage" function for platform: ${platform}`
    );
  }

  try {
    // Call postMessage off the `rover` object so that on android it doesn't lose the `this`
    // and we can avoid having postMessage.bind(rover)
    rover.postMessage(
      prepareEventData(platform, {
        action: 'event',
        ...eventNames,
        ...eventData,
      })
    );
  } catch (err) {
    return reportEmbeddedError(`Error firing embedded web event: ${err}`);
  }

  return true;
};

export default fireEmbeddedWebEvent;



// WEBPACK FOOTER //
// ../utilities/fireEmbeddedWebEvent.js